/**
 * The WRITE half of the per-row opener ON/OFF switch (the render half is
 * `armSwitchState.ts`). One operator gesture owes the backend two writes: the
 * pair-level kill switch (`openerEnabled`) and, when the row has a rule, that
 * rule's durable arm intent (`desiredArmed`).
 *
 * They are sequenced ASYMMETRICALLY, because the two directions have opposite
 * failure modes:
 *
 * ON — strictly sequential, kill switch FIRST. The backend gates arming on the
 * pair (`assertPairOpenerEnabled` in position-opener rejects an activation
 * whose pair is `openerEnabled: false`), so arming before or without the
 * enable is not merely incoherent — it is refused. A failed enable therefore
 * ABORTS the gesture: firing the arm anyway would only add a second toast for
 * a write that cannot succeed.
 *
 * OFF — both writes are attempted regardless of each other. The disarm is the
 * safety-critical half of an OFF gesture, and gating it behind the kill switch
 * meant a failing kill-switch PATCH left the rule ARMED after the operator had
 * asked for it off, with nothing on screen saying the rule was still live.
 * Disarming a rule whose market data is still on is perfectly coherent (that
 * is just a pause), so there is no ordering left to protect. Both writes carry
 * "off", so running them together cannot race into a re-arm.
 */
export async function runOpenerToggleWrites(opts: {
  /** The operator's intent: `true` = arm, `false` = disarm. */
  next: boolean;
  /** Flips `prediction_pairs.openerEnabled` on the opener-orchestrator. */
  setPairOpenerEnabled: (enabled: boolean) => Promise<unknown>;
  /**
   * Writes the rule's durable `desiredArmed` to its OWNING feed pod, or `null`
   * when the row has no rule yet (the kill switch is then the whole gesture).
   */
  setRuleArmed: ((desiredArmed: boolean) => Promise<unknown>) | null;
}): Promise<boolean> {
  const { next, setPairOpenerEnabled, setRuleArmed } = opts;

  if (next) {
    // Rejections propagate: the caller's catch drops the pending overlay and
    // the global MutationCache.onError backstop raises the toast.
    await setPairOpenerEnabled(true);
    if (setRuleArmed) await setRuleArmed(true);
    return true;
  }

  const outcomes = await Promise.allSettled([
    setPairOpenerEnabled(false),
    setRuleArmed ? setRuleArmed(false) : Promise.resolve()
  ]);
  return outcomes.every((o) => o.status === 'fulfilled');
}
